// @givmo/app-admin — dashboard decode + format helpers.
//
// Pure, side-effect-free helpers that map the live dashboard wire shapes (CompanyDashboard,
// CompanyDonationVolume, CompanyTopCharities) into the values the SaaS dashboard renders. Kept out of
// the component so the money/delta/date formatting + the activity→approval-row and activity→
// company-gave-row mappings are unit-testable in isolation (mirrors `employees-decode.ts`).
//
// MONEY: the new dashboard scalars + top-charity totals are JSON numbers in DOLLARS. The
// `amount_entered` on a DonationMatchOut is a STRING — formatted for display via `Number()` (no
// arithmetic round-trips). COUNTS are ints. Grouped (ETF/fundraiser) legs are CENT-LEVEL splits of
// the group total (the backend splits `Decimal(cents)/100` — a $50 ETF over 6 charities produces
// "8.33"/"8.34" legs), so grouped sums run in EXACT integer cents via `parseCentsAmount`.
//
// ★ CENTS ARE CHARGEABLE, and the KPI tiles round while the APPROVALS surface must not. `formatUsd` /
// `formatAmountEntered` are the WHOLE-DOLLAR display formatters — correct for the headline scalars,
// and NEVER correct for a number about to be charged: rounding "$590.24" to "$590" on an approval row
// is what hid a dead Approve button from a real customer for a week. Anything that prices, sums, or
// labels an approval goes through the exact-cent path (`parseCentsAmount` → `parseChargeableAmount` /
// `chargeableCents` → `formatCentsUsd` / `formatApprovalUsd`).

import type {
  CharityOut,
  CompanyDashboard,
  CompanyDashboardActivity,
  DonationMatchOut,
  DonationMatchStatus,
  WalletOut,
} from '@givmo/api-client';
import {
  disclosureEcho,
  type DisclosureEchoFields,
  type ResolvedDisclosure,
} from '@givmo/disclosure';
import type { StatedCharge } from '../../components/charge-quote';
import { formatMoneyWithSymbol } from '../settings/wallet-decode';

/**
 * Surfaced approval statuses. The dashboard never shows "Completed" — it collapses to Approved.
 *
 * `Cancelled` is a request withdrawn with NO admin decision on it: the requester's link to the
 * company ended before anyone acted, whoever ended it — their account deletion (D-CAM-29) or an
 * unlink by either side (D-CAM-32). It is never a rejection, so it never reads as one (CAM-R33): a
 * neutral badge, and its own filter rather than the Rejected one. `Unknown` is the tolerant-reader
 * bucket for a wire value this build does not know: neutral, labelled verbatim, and offered no
 * actions, because an action on an unrecognized status could contradict the backend's state rules.
 */
export type ApprovalStatus = 'Pending' | 'Approved' | 'Rejected' | 'Cancelled' | 'Unknown';

export const BADGE_TONE: Record<ApprovalStatus, 'warning' | 'success' | 'danger' | 'neutral'> = {
  Pending: 'warning',
  Approved: 'success',
  Rejected: 'danger',
  Cancelled: 'neutral',
  Unknown: 'neutral',
};

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const PERCENT = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1,
});

/** Format a dollars-as-number value (or null/undefined → "—") as whole-dollar USD. */
export function formatUsd(dollars: number | null | undefined): string {
  if (dollars === null || dollars === undefined || !Number.isFinite(dollars)) return '—';
  return USD.format(dollars);
}

/**
 * Format the STRING `amount_entered` (dollars) for display. Uses `Number()` purely for the formatter
 * — never for arithmetic that round-trips through float. Falls back to "—" for blank/unparseable.
 */
export function formatAmountEntered(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) return '—';
  const trimmed = raw.trim();
  if (trimmed.length === 0) return '—';
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return '—';
  return USD.format(n);
}

/** Whole-number count formatter ("1,284"); blank for null/undefined. */
export function formatCount(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US');
}

export interface StatDelta {
  /** Signed percent label, e.g. "+12.5%". */
  text: string;
  direction: 'up' | 'down';
}

/**
 * Year-over-year percent delta of `current` vs `prior`. Returns `null` (→ omit the delta chip)
 * whenever it cannot be computed cleanly: either bound missing, or `prior` is 0 (÷0 → never NaN /
 * "Infinity%"). A zero change is rendered as "0%" up.
 */
export function yoyDelta(
  current: number | null | undefined,
  prior: number | null | undefined,
): StatDelta | null {
  if (current === null || current === undefined || !Number.isFinite(current)) return null;
  if (prior === null || prior === undefined || !Number.isFinite(prior)) return null;
  if (prior === 0) return null;
  const pct = ((current - prior) / prior) * 100;
  if (!Number.isFinite(pct)) return null;
  const sign = pct > 0 ? '+' : '';
  return {
    text: `${sign}${PERCENT.format(pct)}%`,
    direction: pct < 0 ? 'down' : 'up',
  };
}

/**
 * Map a backend match status onto a surfaced ApprovalStatus (Completed → Approved). Never throws and
 * never returns undefined, whatever the wire carries.
 *
 * Deliberately asymmetric. The four values that decide whether a row offers Approve/Decline match
 * EXACTLY, so a differently-cased "pending" is not quietly made actionable. `Cancelled`, the fifth
 * value the committed OpenAPI snapshot declares (CAM-R36), matches in any casing and either
 * spelling, because the status carries no action. Everything else is `Unknown`.
 */
export function toApprovalStatus(
  status: DonationMatchStatus | string | null | undefined,
): ApprovalStatus {
  switch (status) {
    case 'Pending':
      return 'Pending';
    case 'Rejected':
      return 'Rejected';
    case 'Approved':
    case 'Completed':
      return 'Approved';
  }
  const folded = typeof status === 'string' ? status.trim().toLowerCase() : '';
  return folded === 'cancelled' || folded === 'canceled' ? 'Cancelled' : 'Unknown';
}

/**
 * The badge text for a row whose legs carry `raw` wire statuses. A known status reads as itself. An
 * `Unknown` one names the wire value verbatim when every leg carries the same readable one — a status
 * added after this build ships still says what it is — and plain "Unknown" otherwise. Never blank.
 */
export function approvalStatusLabel(status: ApprovalStatus, raw: readonly unknown[]): string {
  if (status !== 'Unknown') return status;
  const values = new Set(raw.map((value) => (typeof value === 'string' ? value.trim() : '')));
  const [only] = values;
  return values.size === 1 && only ? only : 'Unknown';
}

/** "YYYY-MM" period → short month label ("2026-01" → "Jan"). Falls back to the raw string. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function periodToMonth(period: string): string {
  const m = /^\d{4}-(\d{2})$/.exec(period);
  if (!m) return period;
  const idx = Number(m[1]) - 1;
  return MONTHS[idx] ?? period;
}

/** Format an ISO datetime as a short "MMM D" (e.g. "Jun 24"). Falls back to the raw string. */
export function formatRequestedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** The minimal user shape the activity row carries (typed `unknown | null` on the wire). */
export interface ActivityUser {
  id?: string;
  first_name?: string | null;
  last_name?: string | null;
  username?: string | null;
  logo_url?: string | null;
}

/** Narrow the `unknown | null` activity `user` to the fields the row renders. */
export function readActivityUser(user: unknown): ActivityUser | null {
  if (user === null || typeof user !== 'object') return null;
  return user as ActivityUser;
}

/** Full display name for an activity user, guarding nulls; falls back to username/"Employee". */
export function activityUserName(user: ActivityUser | null): string {
  if (!user) return 'Employee';
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  if (name.length > 0) return name;
  return user.username || 'Employee';
}

/**
 * D-CAM-68's words for an employee who ticked "Hide my name", wherever their name would stand on its
 * own: the Approvals list, the decline prompt, Needs attention (EP-WEB39).
 */
export const ANONYMOUS_EMPLOYEE = 'An anonymous employee';

/** The same words inside a sentence: "Matching an anonymous employee’s gift". */
const ANONYMOUS_EMPLOYEE_IN_SENTENCE = 'an anonymous employee';

/** Fields shared by both approval-row shapes. */
interface ApprovalRowBase {
  /**
   * Stable row key: the donation-match id for a single (the `{match_id}` the single approve/reject
   * routes key on); the request's `request_id` for a grouped row (CAM-R163), or — from a backend
   * before it — the legs' shared `post_id`.
   */
  id: string;
  /** Who asked, as the row names them: their name, or {@link ANONYMOUS_EMPLOYEE}. */
  employeeName: string;
  /** Their avatar; null for an employee who hid their name. */
  employeeLogoUrl: string | null;
  /**
   * The employee ticked "Hide my name" (D-CAM-107): the row names no one — `employeeName` is
   * {@link ANONYMOUS_EMPLOYEE} and its avatar shows no initials. In a sentence it reads
   * {@link employeeInSentence}.
   */
  anonymous: boolean;
  /**
   * Display amount (D-EP-56: what the company matches, never the employee's gift): a waiting
   * request's match now ("$200"; "$0" when its budget is used up), a decided request's matched
   * amount, or "—" (declined, cancelled, or no figure on the wire).
   */
  amount: string;
  /** A waiting request's short budget note under its amount (`matchFigureNote`), or null. */
  amountNote: string | null;
  /**
   * What approving this row matches, in DOLLARS from exact integer cents: the server's own cap for
   * the request (`approvable.amount`, EP-BE19) — what the approve dialog opens on, re-checks and
   * charges (the backend `DonationMatchApprovalRequest.amount` is a Decimal, so cents are fine).
   * `null` when the row cannot be approved from the list (decided, its budget used up, or no figure
   * on the wire) → its Approve stays inert. Display and charge always agree: the dialog never
   * charges a number other than the one shown.
   */
  amountValue: number | null;
  /** A waiting request's figure as the server sent it; null on a decided row. */
  figure: MatchFigure | null;
  requestedAt: string;
  /**
   * When the request was made (epoch ms; null when the wire's timestamp does not parse): waiting
   * requests claim the match budgets in this order, oldest first (D-EP-56), and Approve all takes
   * them in it.
   */
  requestedAtMs: number | null;
  status: ApprovalStatus;
  /** The status badge's text (`approvalStatusLabel`): the status itself, or an unknown wire value. */
  statusLabel: string;
  /**
   * The member donation-match ids the write path operates on: `[id]` for a single; EVERY leg for a
   * grouped row — the grouped routes REQUIRE the post's complete pending set (the backend
   * `_load_complete_grouped_matches` rejects a partial batch).
   */
  matchIds: string[];
}

/** A standalone single-charity match request (one activity row → one display row). */
export interface SingleApprovalRow extends ApprovalRowBase {
  kind: 'single';
  charity: CharityOut;
}

/**
 * One member charity of a grouped request — what makes the group's composition INSPECTABLE (spec
 * §5): an admin approving a grouped charge sees exactly which charities the money splits across.
 * A decided request shows each charity's matched slice; a waiting one names the charities only,
 * because its legs' amounts are slices of the employee's gift (D-EP-56) and the match's own split is
 * fixed only when it is charged — except a collection gift's, whose share of the match the server
 * serves on `/pending` (`collection-shares.ts`, EP-WEB40).
 */
export interface GroupedMember {
  /** The leg's match id: what a waiting collection gift's share is keyed by. */
  matchId: string;
  charityName: string;
  /** The charity's logo, for the opened charities (initials when absent). */
  logoUrl: string | null;
  /** The leg's matched amount on an approved request ("$8.33"), else null (not shown). */
  amount: string | null;
}

// ── A charity-collection gift's words (EP-WEB40; Company Profiles' CAM-R136, D-CAM-111 and 113) ──

/**
 * The charity collection a grouped request's gift went to, as the dashboard serves it (`collection`,
 * backend `match_collection_label.py`): its name and cover while the collection's identity may reach
 * anyone but its owner (Public or Unlisted, active, not unlisted by moderation) and the employee did
 * not hide their name; and whether it is Private. Never the collection's id.
 */
export interface CollectionLabel {
  /** The collection's name, or null where the server names it for no one. */
  name: string | null;
  /** Its cover, only beside its name. */
  coverUrl: string | null;
  isPrivate: boolean;
}

/** A Private collection, on every company surface (Jonathan, 2026-10-05). */
export const PRIVATE_CHARITY_COLLECTION = 'Private Charity Collection';

/**
 * A collection the server names for no one (D-CAM-113): an anonymous request's, whoever made it, and
 * one deleted or hidden by moderation since the gift.
 */
export const CHARITY_COLLECTION = 'Charity Collection';

/**
 * Read the dashboard row's `collection` block; null when the row is not a collection gift. Tolerant:
 * a blank name names nothing, a cover rides only with a name, and a block that does not say whether
 * it is Private reads as Private, the reading that names nothing (the apps read it the same way).
 */
export function readCollectionLabel(raw: unknown): CollectionLabel | null {
  if (raw === null || typeof raw !== 'object') return null;
  const wire = raw as Record<string, unknown>;
  const name =
    typeof wire.name === 'string' && wire.name.trim().length > 0 ? wire.name.trim() : null;
  const cover =
    name !== null && typeof wire.cover_image === 'string' && wire.cover_image.trim().length > 0
      ? wire.cover_image.trim()
      : null;
  return {
    name,
    coverUrl: cover,
    isPrivate: typeof wire.is_private === 'boolean' ? wire.is_private : true,
  };
}

/** What the collection is called wherever it stands alone: its name, or the words for it. */
export function collectionTitle(label: CollectionLabel): string {
  if (label.name !== null) return label.name;
  return label.isPrivate ? PRIVATE_CHARITY_COLLECTION : CHARITY_COLLECTION;
}

/**
 * The collection inside a sentence, after "donated to" or "to" (D-CAM-111): "the {name} collection"
 * (the article and the word, as "the X fundraiser" reads; a name already ending in "collection" does
 * not repeat it), "a Private Charity Collection", "a Charity Collection". `name` is the part set in
 * bold.
 */
export function collectionPhrase(label: CollectionLabel): {
  lead: string;
  name: string;
  trail: string;
} {
  if (label.name === null) return { lead: 'a ', name: collectionTitle(label), trail: '' };
  const words = label.name
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  return {
    lead: 'the ',
    name: label.name,
    trail: words[words.length - 1] === 'collection' ? '' : ' collection',
  };
}

/** Whether the collection's mark is the neutral lock rather than its cover: one it does not name. */
export function collectionLocked(label: CollectionLabel): boolean {
  return label.name === null;
}

/**
 * A grouped (ETF / fundraiser / collection) match request: a split gift's N activity legs — one
 * request — collapsed into ONE display row, mirroring the production Flutter webapp's single
 * "requested a match to <ETF>" tile. Approve/Decline for this row MUST go through the grouped
 * routes over its `matchIds`: the single-match routes reject grouped posts server-side (400). Nothing
 * here reads the legs' post: an anonymous request's `post_id` names its employee and is withheld
 * (Company Profiles' CAM-R164).
 */
export interface GroupedApprovalRow extends ApprovalRowBase {
  kind: 'grouped';
  /**
   * Display name: a collection gift's title (`collectionTitle`), else `group_name`, else the
   * type-matched etf/fundraiser name; null when unnamed.
   */
  groupName: string | null;
  /**
   * The charity collection the gift went to (EP-WEB40), or null for any other grouped request. On a
   * request whose employee hid their name it is never named, whatever the wire says (D-CAM-113).
   */
  collection: CollectionLabel | null;
  /** 'ETF' / 'Fundraiser' when the wire declares the group type; null otherwise (e.g. collections). */
  groupKindLabel: string | null;
  /**
   * The member charities with their split amounts, in row order — the count badge, the decline copy,
   * and the row's disclosure panel all read this. Per-charity amounts exist because the group's
   * composition is disclosed, not tooltip'd (spec §5 / D-EP-18).
   */
  members: GroupedMember[];
}

/** A display-ready row for the pending-approvals table. */
export type MatchApprovalRow = SingleApprovalRow | GroupedApprovalRow;

/** The grouped row's display title: its name, else "N charities" (the Flutter headline fallback). */
export function groupRowTitle(row: GroupedApprovalRow): string {
  return row.groupName ?? charityCountLabel(row.members.length);
}

/** "1 charity" / "6 charities". */
export function charityCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'charity' : 'charities'}`;
}

/**
 * Parse a wire money string (dollars, e.g. `amount_entered` = `"50"` / `"50.00"`) to the WHOLE-DOLLAR
 * INTEGER the approve body needs. Returns `null` for blank/unparseable/non-positive/non-integer input
 * so the caller can keep a non-chargeable row inert (we NEVER send a fractional or ≤0 amount — the
 * backend requires `gt=0`, and the whole-dollar convention is integer-only; a value with cents is
 * rejected rather than silently rounded, so we never charge a different number than we displayed).
 */
export function parseWholeDollarAmount(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined) return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) return null;
  return n;
}

/**
 * The chargeable amount for an APPROVAL row, in dollars, derived from EXACT integer cents.
 *
 * ★ Cents are chargeable. The backend's `DonationMatchApprovalRequest.amount` is
 * `Decimal = Field(gt=0)` — it has no whole-dollar constraint, and `_compute_allowed_amount`
 * deliberately quantizes to cents (PIH-BE8) rather than truncating. An EXTERNAL donation carries a
 * real-world amount ($590.24), so a whole-dollar-only rule made every such match permanently
 * unapprovable while the rounded display ("$590") hid the reason — the button just died. Parse in
 * exact integer cents (never float arithmetic on the wire string), then express as dollars for the
 * wire body. `null` only for blank / unparseable / non-positive input.
 */
export function parseChargeableAmount(raw: string | null | undefined): number | null {
  const cents = parseCentsAmount(raw);
  if (cents === null || cents <= 0) return null;
  return cents / 100;
}

/**
 * Dollars → exact integer cents for a value that CAME FROM `parseChargeableAmount` / a cent-split
 * sum. `Math.round` is the inverse of the `cents / 100` those produce (well inside 2^53), so this
 * never invents a fraction of a cent — it recovers the integer the value was built from.
 */
export function chargeableCents(amount: number): number {
  return Math.round(amount * 100);
}

/**
 * Parse a wire money string (dollars, up to 2 decimals) to EXACT integer CENTS via string math — no
 * float arithmetic. Grouped legs need this: they are cent-level splits of the group total (see the
 * money note at the top), so per-leg whole-dollar parsing would mark every real ETF group
 * unpriceable even though the SUM is a clean whole-dollar amount. Returns `null` for blank /
 * unparseable / negative / beyond-2-decimals input so a malformed leg poisons its group's total.
 */
export function parseCentsAmount(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined) return null;
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(raw.trim());
  if (m === null) return null;
  const whole = Number(m[1]);
  if (!Number.isSafeInteger(whole * 100)) return null;
  return whole * 100 + Number(((m[2] ?? '') + '00').slice(0, 2));
}

const USD_CENTS = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Whole dollars when the cent total is even ("$50"), exact cents otherwise ("$16.66"); "—" on null. */
export function formatCentsUsd(cents: number | null): string {
  if (cents === null || !Number.isFinite(cents)) return '—';
  return cents % 100 === 0 ? USD.format(cents / 100) : USD_CENTS.format(cents / 100);
}

/**
 * Derive the single displayed status for a grouped row's legs. The backend transitions a grouped
 * batch atomically (an account deletion cancels every pending leg together), so legs are homogeneous
 * in practice; this stays truthful if they ever aren't: any pending leg → the group still needs a
 * decision; any approved leg → money moved; all rejected or all cancelled → that. Any other mix has
 * no honest single word, so it is `Unknown` — neutral, and offered no action.
 */
export function deriveGroupStatus(statuses: ApprovalStatus[]): ApprovalStatus {
  if (statuses.includes('Pending')) return 'Pending';
  if (statuses.includes('Approved')) return 'Approved';
  if (statuses.length > 0 && statuses.every((s) => s === 'Rejected')) return 'Rejected';
  if (statuses.length > 0 && statuses.every((s) => s === 'Cancelled')) return 'Cancelled';
  return 'Unknown';
}

/** An activity row narrowed to the match-carrying shape the approvals table renders. */
type MatchActivityLeg = CompanyDashboardActivity & { donation_match: DonationMatchOut };

/**
 * Whether a request's employee ticked "Hide my name": ANY leg's flag, on either copy — the gift's
 * `donation.hide_name` or its match's `is_anonymous` — as the server reads it (CAM-R134, which then
 * sends no `user`). Read before the `user` block, so a request whose employee hid their name is never
 * named, even by a wire that sends a name beside the flag.
 */
function requestHidesName(legs: readonly MatchActivityLeg[]): boolean {
  return legs.some(
    (leg) => leg.donation_match.is_anonymous === true || leg.donation?.hide_name === true,
  );
}

/** Who asked for a request, as its row names them (EP-WEB39). */
function requestEmployee(
  legs: readonly MatchActivityLeg[],
): Pick<ApprovalRowBase, 'employeeName' | 'employeeLogoUrl' | 'anonymous'> {
  if (requestHidesName(legs)) {
    return { employeeName: ANONYMOUS_EMPLOYEE, employeeLogoUrl: null, anonymous: true };
  }
  // The shared requester: the first leg's (the backend enforces a homogeneous batch).
  const user = readActivityUser(legs[0]?.user);
  return {
    employeeName: activityUserName(user),
    employeeLogoUrl: user?.logo_url ?? null,
    anonymous: false,
  };
}

/**
 * The collection a request's gift went to: the label any leg carries (every leg is the same post's).
 * A request whose employee hid their name never names its collection or shows its cover, even from
 * a wire that sends them: the server withholds both (CAM-R134), and the request's flags are read
 * first, as for the employee (D-CAM-113: a collection's name and its creator can lead to the
 * employee).
 */
function requestCollection(legs: readonly MatchActivityLeg[]): CollectionLabel | null {
  const label = legs.map((leg) => readCollectionLabel(leg.collection)).find((l) => l !== null);
  if (label === undefined) return null;
  return requestHidesName(legs) ? { ...label, name: null, coverUrl: null } : label;
}

/** The row's employee inside a sentence ("Matching {…}’s gift"): their name, or "an anonymous
 *  employee" (D-CAM-68). */
export function employeeInSentence(
  row: Pick<ApprovalRowBase, 'employeeName' | 'anonymous'>,
): string {
  return row.anonymous ? ANONYMOUS_EMPLOYEE_IN_SENTENCE : row.employeeName;
}

/**
 * What approving a WAITING request would match (EP-BE19, D-EP-56), read from the server's
 * `approvable`. An admin is shown this and never the employee's gift (`donation.amount_entered`,
 * which the wire still carries). Waiting requests claim the match budgets in request order (oldest
 * first), so each figure is what is left for that request after the approved matches and the
 * requests before it: the figure its own cap route answers and the most its approval accepts, and
 * one employee's figures add up to their budget.
 *
 * - `available`: approving it matches `cents` now.
 * - `used-up`: less than $1 is left for it (CAM-R79) — of the employee's budget, the company's
 *   annual one or a Group member's contribution; every case reads the same to an admin.
 * - `unknown`: no figure on the wire (a backend before EP-BE19, or a malformed one): the row shows
 *   "—" and cannot be approved from the list — never a fallback to the gift.
 */
export type MatchFigure =
  | { kind: 'available'; cents: number }
  | { kind: 'used-up' }
  | { kind: 'unknown' };

const USED_UP_STATUSES: ReadonlySet<string> = new Set([
  'employee_budget_used_up',
  'company_budget_used_up',
  'contribution_used_up',
]);

/** Read one leg's `approvable` into a {@link MatchFigure}. Never throws, whatever the wire holds. */
export function readMatchFigure(approvable: unknown): MatchFigure {
  if (approvable === null || typeof approvable !== 'object') return { kind: 'unknown' };
  const wire = approvable as Record<string, unknown>;
  const status = wire.status;
  if (typeof status === 'string' && USED_UP_STATUSES.has(status)) return { kind: 'used-up' };
  if (status !== 'available') return { kind: 'unknown' };
  const cents = parseCentsAmount(wireAmountString(wire.amount));
  if (cents === null || cents <= 0) return { kind: 'unknown' };
  return { kind: 'available', cents };
}

/** Whether two legs' figures are the same request's (every leg of a grouped request carries it). */
function sameFigure(a: MatchFigure, b: MatchFigure): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** One phrase for every budget with nothing left for a request (Jonathan, 2026-10-05). */
export const NO_BUDGET_LEFT = 'No budget left';

/** The note under a waiting request's amount: {@link NO_BUDGET_LEFT} when nothing is left for
 *  it, else null. */
export function matchFigureNote(figure: MatchFigure | null): string | null {
  return figure !== null && figure.kind === 'used-up' ? NO_BUDGET_LEFT : null;
}

/** A row's amount fields from its status and figure (D-EP-56: never the employee's gift). */
function rowAmounts(
  status: ApprovalStatus,
  figure: MatchFigure | null,
  matchedCents: number | null,
): { amount: string; amountNote: string | null; amountValue: number | null } {
  if (status === 'Pending' && figure !== null) {
    if (figure.kind === 'available') {
      return {
        amount: formatCentsUsd(figure.cents),
        amountNote: matchFigureNote(figure),
        amountValue: figure.cents / 100,
      };
    }
    if (figure.kind === 'used-up') {
      return { amount: formatCentsUsd(0), amountNote: matchFigureNote(figure), amountValue: null };
    }
    return { amount: '—', amountNote: null, amountValue: null };
  }
  // A decided request shows what was matched; declined, cancelled and unknown show "—".
  if (status === 'Approved') {
    return { amount: formatCentsUsd(matchedCents), amountNote: null, amountValue: null };
  }
  return { amount: '—', amountNote: null, amountValue: null };
}

/** An approved leg's matched amount — the match's own `amount_entered`, then `matched_amount`. */
function legMatchedSource(a: MatchActivityLeg): string | null | undefined {
  return a.donation_match.amount_entered ?? wireAmountString(a.matched_amount);
}

/**
 * Collapse one split gift's N legs into ONE display row (mirrors the Flutter webapp's
 * `GroupedMatchActivityItem`): the row is named for the group, priced at the request's figure (read
 * once), the employee is the shared requester (first leg; the backend enforces a homogeneous batch),
 * and the member match ids carry the complete set the grouped routes require.
 */
function buildGroupedRow(id: string, legs: MatchActivityLeg[]): GroupedApprovalRow {
  const first = legs[0]!;
  const legStatuses = legs.map((l) => l.donation_match.status);
  const status = deriveGroupStatus(legStatuses.map(toApprovalStatus));

  // A waiting request's figure is the REQUEST's: every leg carries the same `approvable`, read once
  // and never summed. Legs that disagree, or one without a figure, leave the row unpriced — never a
  // wrong total on a charge.
  const legFigures = legs.map((leg) => readMatchFigure(leg.approvable));
  const figure =
    status !== 'Pending'
      ? null
      : legFigures.every((f) => sameFigure(f, legFigures[0]!))
        ? legFigures[0]!
        : ({ kind: 'unknown' } as const);

  // An approved request's matched total and each charity's slice, in exact cents. One unpriceable
  // leg poisons the total to null (never a wrong sum).
  let matchedCents: number | null = 0;
  const members: GroupedMember[] = [];
  for (const leg of legs) {
    const legCents = status === 'Approved' ? parseCentsAmount(legMatchedSource(leg)) : null;
    members.push({
      matchId: leg.donation_match.id,
      charityName: leg.charity.name,
      logoUrl: leg.charity.logo_url ?? null,
      amount: status === 'Approved' ? formatCentsUsd(legCents) : null,
    });
    matchedCents = matchedCents === null || legCents === null ? null : matchedCents + legCents;
  }

  const groupType = first.group_type ?? null;
  const explicitName = first.group_name?.trim();
  const typedName = (groupType === 'fundraiser' ? first.fundraiser_name : first.etf_name)?.trim();
  const collection = requestCollection(legs);
  const groupName =
    collection !== null ? collectionTitle(collection) : explicitName || typedName || null;

  return {
    kind: 'grouped',
    id,
    ...requestEmployee(legs),
    groupName,
    collection,
    groupKindLabel: groupType === 'fundraiser' ? 'Fundraiser' : groupType === 'etf' ? 'ETF' : null,
    members,
    ...rowAmounts(status, figure, matchedCents),
    figure,
    requestedAt: formatRequestedAt(first.donation_match.created_at),
    requestedAtMs: earliestRequestMs(legs.map((l) => l.donation_match.created_at)),
    status,
    statusLabel: approvalStatusLabel(status, legStatuses),
    matchIds: legs.map((l) => l.donation_match.id),
  };
}

/**
 * Whether the dashboard's match-activity feed was CUT at the backend page boundary — i.e. more
 * company match rows exist than were returned on this (only) page. The dashboard fetches a SINGLE
 * page (offset 0, the default `page[limit]`) and renders it as "all", so when this is true the LAST
 * grouped row may be missing trailing legs (its siblings live on a page the dashboard never fetches).
 *
 * The wire `total` counts the paginated MATCH rows PLUS the direct-donation groups (which the backend
 * prepends to page 0 only), so the match-row total is `total − directCount`. Truncated ⇔ the match
 * legs returned here are fewer than that match-row total. Undetectable (→ `false`) when `total` is
 * absent — the tolerant-reader default is to NOT drop.
 */
export function isActivityTruncated(dashboard: CompanyDashboard | undefined): boolean {
  if (!dashboard) return false;
  const activity = dashboard.donation_matching_activity;
  const matchLegs = activity.filter((a) => a.donation_match != null).length;
  const directCount = activity.length - matchLegs;
  const total = dashboard.total;
  if (total === null || total === undefined || !Number.isFinite(total)) return false;
  return matchLegs < total - directCount;
}

/** A standalone match's row: one leg, routed by its own match id. */
function buildSingleRow(a: MatchActivityLeg): SingleApprovalRow {
  const match = a.donation_match;
  const status = toApprovalStatus(match.status);
  const figure = status === 'Pending' ? readMatchFigure(a.approvable) : null;
  return {
    kind: 'single',
    id: match.id,
    ...requestEmployee([a]),
    charity: a.charity,
    // Display + charge both derive from the SAME exact-cent figure, so the row can never show a
    // number it refuses to charge (the rounded `formatAmountEntered` once rendered "$590" for a
    // $590.24 match whose Approve was permanently inert).
    ...rowAmounts(status, figure, parseCentsAmount(legMatchedSource(a))),
    figure,
    requestedAt: formatRequestedAt(match.created_at),
    requestedAtMs: earliestRequestMs([match.created_at]),
    status,
    statusLabel: approvalStatusLabel(status, [match.status]),
    matchIds: [match.id],
  };
}

/**
 * The row's request (CAM-R163): the OPAQUE `request_id` the dashboard serves on every match row —
 * the same on every leg of one request, wherever a page cuts it, and the value `/pending` and
 * `/inbox` serve for it; null on the company's own gifts. Read by hand because the committed
 * snapshot predates the field (`agent_docs/api-client.md` § "Additive RESPONSE fields the snapshot
 * lacks"): the regen that declares it replaces this read with the generated field. Only a non-empty
 * string is an identity; anything else reads as absent, and the leg groups as it did before.
 */
export function readRequestId(activity: unknown): string | null {
  if (activity === null || typeof activity !== 'object') return null;
  const value = (activity as { request_id?: unknown }).request_id;
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/** The legs of one request, in feed order, and what made them one. */
interface RequestLegs {
  /** `request` — they share a `request_id`; `post` — a grouped post, from a backend before it;
   *  `match` — a match alone. */
  by: 'request' | 'post' | 'match';
  key: string;
  legs: MatchActivityLeg[];
}

/**
 * A request is a split gift — ONE grouped row, approved and declined through the grouped routes over
 * every leg — when its legs are on a grouped post (`is_grouped`), or when it has more than one leg:
 * a departed requester's waiting split gift keeps its legs and its one `request_id` after its post
 * goes with their account (`is_grouped` false, `post_id` null; Company Profiles' CAM-R127).
 */
function isSplitGift(request: RequestLegs): boolean {
  if (request.by === 'post') return true;
  if (request.by === 'match') return false;
  return request.legs.length > 1 || request.legs.some((leg) => leg.is_grouped === true);
}

/**
 * Map the dashboard's `donation_matching_activity` to approval rows: keep ONLY rows that carry a
 * `donation_match` (the match rows; backend returns them pending-first), then collapse each request's
 * legs into ONE display row (07-CUT0 ①: an ETF/fundraiser request is ONE request, not N charity
 * rows) — order-preserving on each request's first appearance. A waiting row's amount is the server's
 * figure for it (`readMatchFigure`), which every leg of a request carries: read once, never summed.
 *
 * WHAT MAKES LEGS ONE REQUEST (EP-WEB38): their `request_id` (CAM-R163) whenever the wire carries it —
 * never their post. An anonymous request's `post_id` leads to its employee (`GET /posts/{id}` names a
 * Public post's author), so Company Profiles' CAM-R164 withholds it once this grouping serves in prod,
 * and nothing in this module reads a match row's post. Grouping by `request_id` also keeps apart what
 * a post would merge: a request asked again on the same post is a request of its own, and the legs of
 * a post that lost its grouping are a request each, as `/pending` lists them. Only a feed from a
 * backend before CAM-R163 (no `request_id`) falls back to the post: a grouped post's legs together,
 * every other match alone.
 *
 * PAGE-TRUNCATION GUARD (EP-FIX1 ③): the match feed is a single paginated page. When it is CUT at the
 * page boundary (`isActivityTruncated`), the LAST grouped row may be missing trailing legs — which
 * would render a PARTIAL group (wrong charity count / wrong total, or an unpriceable group) and,
 * worse, drive the grouped approve/decline routes with an INCOMPLETE `match_ids` set that the backend
 * hard-400s (`_load_complete_grouped_matches` requires EVERY pending sibling of the post). The wire
 * carries no group-size field, so a group's completeness can't be verified; but a group's legs are
 * contiguous, so only the TAIL group can straddle the cut. We therefore DROP that one
 * potentially-incomplete grouped row rather than show or charge a wrong total. Singles are always
 * whole (one leg); a complete group that happens to end exactly at the boundary is dropped
 * conservatively and self-heals on the next load once earlier rows resolve.
 */
export function toApprovalRows(dashboard: CompanyDashboard | undefined): MatchApprovalRow[] {
  if (!dashboard) return [];
  const legs = dashboard.donation_matching_activity.filter(
    (a): a is MatchActivityLeg => a.donation_match != null,
  );

  const requests: RequestLegs[] = [];
  const byKey = new Map<string, RequestLegs>();
  const add = (by: RequestLegs['by'], key: string, leg: MatchActivityLeg) => {
    // The namespace keeps a request id, a post id and a match id from ever sharing an entry.
    const existing = byKey.get(`${by}:${key}`);
    if (existing !== undefined) {
      existing.legs.push(leg);
      return;
    }
    const request: RequestLegs = { by, key, legs: [leg] };
    byKey.set(`${by}:${key}`, request);
    requests.push(request);
  };

  for (const a of legs) {
    const requestId = readRequestId(a);
    if (requestId !== null) {
      add('request', requestId, a);
      continue;
    }
    // A backend before CAM-R163. Prefer the match's own `post_id`; fall back to the activity-level
    // `post_id` (the grouped parent). A leg flagged grouped WITHOUT a post id cannot batch — it stays
    // a single row (the single routes accept a match with no post).
    const postId = a.donation_match.post_id ?? a.post_id ?? null;
    if (a.is_grouped === true && postId !== null && postId.length > 0) {
      add('post', postId, a);
      continue;
    }
    add('match', a.donation_match.id, a);
  }

  const rows = requests.map((request) =>
    isSplitGift(request)
      ? buildGroupedRow(request.key, request.legs)
      : buildSingleRow(request.legs[0]!),
  );

  // Drop the tail grouped row when the feed is truncated (see the doc comment above): only the last
  // group can straddle the page cut, and a single tail row is always complete.
  if (
    isActivityTruncated(dashboard) &&
    rows.length > 0 &&
    rows[rows.length - 1]!.kind === 'grouped'
  ) {
    return rows.slice(0, -1);
  }
  return rows;
}

// ── "Company gave" rows (EP-WEB2) ─────────────────────────────────────────────────────────────────
//
// The direct (company-funded) donation rows the backend UNIONs into `donation_matching_activity`
// (CAM-BE4/BE7: `donation_match: null`, `user: null`, `matched_amount` = the company gift). These are
// the rows `toApprovalRows` deliberately drops — money the company ALREADY moved, with no approval
// semantics — so they get their own read-only projection instead of the filter. The backend collapsed
// a grouped (ETF/fundraiser) gift's splits into ONE row per post (summed `matched_amount`,
// representative charity + group metadata) and prepends direct rows to page 0 only, so this is a
// straight per-row mapping: no grouping machinery, no pending/approve interplay, and no truncation
// interplay (`isActivityTruncated` nets these rows out via `directCount`). Empty until the backend's
// `COMPANY_FUNDING_SOURCE_ROUTING_ENABLED` flag flips — no data ⇒ no render.

/** A direct company donation, projected read-only — the company gave; there is nothing to decide. */
export interface CompanyGaveRow {
  /**
   * Stable row key: the parent post id (unique per direct row — the backend collapses a grouped
   * gift's splits to one row per post), else the donation id, else a positional fallback.
   */
  id: string;
  /** 'grouped' when the gift fanned across an ETF/fundraiser; 'single' for one charity. */
  kind: 'single' | 'grouped';
  /**
   * The gift's charity — for a grouped gift, the REPRESENTATIVE split's charity (display prefers the
   * group metadata). Tolerates a null wire charity (the backend emits `None` when unresolved).
   */
  charity: CharityOut | null;
  /** Grouped display name: `group_name`, else the type-matched etf/fundraiser name; null otherwise. */
  groupName: string | null;
  /** 'ETF' / 'Fundraiser' when the wire declares the group type; null otherwise. */
  groupKindLabel: string | null;
  /**
   * Display amount from the wire `matched_amount` (the company gift; a grouped gift's summed
   * splits) — exact cents when uneven ("$16.66"), whole dollars otherwise; "—" when unpriced.
   */
  amount: string;
  /** Short "MMM D" gift date from the donation's `created_at`; "—" when absent. */
  gaveAt: string;
}

/**
 * `matched_amount` is typed as a string on the wire, but the tolerant-reader posture (and the e2e
 * wire probe, which hedges `string | number | null`) means a raw JSON number must not throw.
 */
function wireAmountString(raw: unknown): string | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? String(raw) : null;
  return typeof raw === 'string' ? raw : null;
}

/**
 * Map the dashboard's DIRECT company-donation rows (`donation_match == null` — exactly the complement
 * of `toApprovalRows`'s keep-set, so every activity row lands in exactly one of the two projections)
 * onto read-only "Company gave" display rows, order-preserving. Never throws; `[]` on no dashboard.
 */
export function toCompanyGaveRows(dashboard: CompanyDashboard | undefined): CompanyGaveRow[] {
  if (!dashboard) return [];
  return dashboard.donation_matching_activity
    .filter((a) => a.donation_match == null)
    .map((a, i): CompanyGaveRow => {
      const grouped = a.is_grouped === true;
      const groupType = a.group_type ?? null;
      // Same name-resolution chain as the grouped approval rows (`buildGroupedRow`).
      const explicitName = a.group_name?.trim();
      const typedName = (groupType === 'fundraiser' ? a.fundraiser_name : a.etf_name)?.trim();
      const createdAt = a.donation?.created_at;
      return {
        id: a.post_id ?? a.donation?.id ?? `direct-${i}`,
        kind: grouped ? 'grouped' : 'single',
        charity: a.charity ?? null,
        groupName: grouped ? explicitName || typedName || null : null,
        groupKindLabel: grouped
          ? groupType === 'fundraiser'
            ? 'Fundraiser'
            : groupType === 'etf'
              ? 'ETF'
              : null
          : null,
        amount: formatCentsUsd(parseCentsAmount(wireAmountString(a.matched_amount))),
        gaveAt: createdAt ? formatRequestedAt(createdAt) : '—',
      };
    });
}

/** The row's display target: the group name for a grouped gift, else the charity name. */
export function companyGaveTarget(row: CompanyGaveRow): string {
  return row.groupName ?? row.charity?.name ?? 'Charity';
}

// ── Approval charge bodies (pure) ────────────────────────────────────────────────────────────────
//
// These build the EXACT wire bodies the money-moving approve routes take, mirrored from the legacy
// Flutter `donation_match_datasource.dart`. Money is a DOLLARS value (cents allowed — the backend
// field is a Decimal); `payment_method` for a
// vaulted card is the fixed string 'Credit Card' (the legacy `PaymentMethod.card.toString()`) and for
// the company wallet is 'Wallet' (the backend `PaymentMethod.WALLET` value — the CARD sub-path issues
// a Shift4 charge, the WALLET sub-path is an in-process company-wallet withdraw with no card).
// `alternative_payment_method_token` / `metadata` are sent as empty objects exactly as the legacy
// datasource sent them; a wallet body sends `card_id: null` (the backend ignores the card on the
// wallet path). Kept pure so the body — the thing that decides how much is charged and from where —
// is asserted in isolation.
//
// Each takes the company-payer `disclosure` its unit was shown (CAM-X3 / CAM-X4 web) and carries it as
// the `shown_disclaimer_*` echo the approve route gates on before any charge; absent, the body is
// byte-identical to before. Each also takes the unit's `charge` (CAM-BE13): the approver's cover-fees
// choice and the gross and fee the dialog stated from the server's quote, which the approve route
// holds the charge to (409 `CHARGE_QUOTE_CHANGED` before any money moves); absent, the body is
// byte-identical too. These fields are declared here ahead of a snapshot regen (the generated
// approval bodies predate them — `agent_docs/api-client.md` § "Additive request fields ahead of a
// regen").

/** A unit's cover-fees choice and the figures the dialog stated for it (CAM-BE13). */
export interface ApprovalCharge {
  readonly coverFees: boolean;
  readonly stated: StatedCharge;
}

/** The body fields a unit's {@link ApprovalCharge} adds: all three, or none without a charge. */
export interface ApprovalChargeFields {
  cover_fees?: boolean;
  expected_amount_charged?: string;
  expected_fee?: string;
}

function approvalChargeFields(charge: ApprovalCharge | null | undefined): ApprovalChargeFields {
  if (!charge) return {};
  return { cover_fees: charge.coverFees, ...charge.stated };
}

/** The `payment_method` value for a saved company card. */
export const CARD_PAYMENT_METHOD = 'Credit Card' as const;

/** The `payment_method` value for a company-wallet-funded approval (backend `PaymentMethod.WALLET`). */
export const WALLET_PAYMENT_METHOD = 'Wallet' as const;

/**
 * The `DonationMatchApprovalRequest` body for a SINGLE match approve
 * (`POST /admin/company/matches/{match_id}/approve`). `amount` is in dollars (cents allowed); the card
 * is sent both as the `card_id` and implied by `payment_method: 'Credit Card'`. Mirrors the legacy
 * datasource's `approveMatch` body byte-for-byte (empty token/metadata objects included).
 */
export function buildApproveMatchBody(params: {
  amount: number;
  cardId: string;
  disclosure?: ResolvedDisclosure | null;
  charge?: ApprovalCharge | null;
}): {
  amount: number;
  payment_method: typeof CARD_PAYMENT_METHOD;
  card_id: string;
  alternative_payment_method_token: Record<string, never>;
  metadata: Record<string, never>;
} & DisclosureEchoFields &
  ApprovalChargeFields {
  return {
    amount: params.amount,
    payment_method: CARD_PAYMENT_METHOD,
    card_id: params.cardId,
    alternative_payment_method_token: {},
    metadata: {},
    ...disclosureEcho(params.disclosure),
    ...approvalChargeFields(params.charge),
  };
}

/**
 * The `GroupedBatchMatchApproval` body for a grouped/ETF multi-charity approve
 * (`POST /admin/company/matches/grouped/approve`). ONE charge over `amount` funds the whole group;
 * the backend splits it across the group's charities. Mirrors the legacy `approveEtfMatchBatch` body.
 */
export function buildApproveGroupedBody(params: {
  matchIds: string[];
  amount: number;
  cardId: string;
  disclosure?: ResolvedDisclosure | null;
  charge?: ApprovalCharge | null;
}): {
  match_ids: string[];
  amount: number;
  payment_method: typeof CARD_PAYMENT_METHOD;
  card_id: string;
  alternative_payment_method_token: Record<string, never>;
  metadata: Record<string, never>;
} & DisclosureEchoFields &
  ApprovalChargeFields {
  return {
    match_ids: params.matchIds,
    amount: params.amount,
    payment_method: CARD_PAYMENT_METHOD,
    card_id: params.cardId,
    alternative_payment_method_token: {},
    metadata: {},
    ...disclosureEcho(params.disclosure),
    ...approvalChargeFields(params.charge),
  };
}

/**
 * The `DonationMatchApprovalRequest` body for a SINGLE match approve funded from the COMPANY WALLET.
 * Same shape/route as {@link buildApproveMatchBody} but `payment_method: 'Wallet'` and `card_id: null`
 * — the backend's WALLET sub-path is an in-process withdraw that ignores the card. No amount is
 * changed vs. the card path (the same entered amount); only the funding source differs.
 */
export function buildApproveWalletMatchBody(params: {
  amount: number;
  disclosure?: ResolvedDisclosure | null;
  charge?: ApprovalCharge | null;
}): {
  amount: number;
  payment_method: typeof WALLET_PAYMENT_METHOD;
  card_id: null;
  alternative_payment_method_token: Record<string, never>;
  metadata: Record<string, never>;
} & DisclosureEchoFields &
  ApprovalChargeFields {
  return {
    amount: params.amount,
    payment_method: WALLET_PAYMENT_METHOD,
    card_id: null,
    alternative_payment_method_token: {},
    metadata: {},
    ...disclosureEcho(params.disclosure),
    ...approvalChargeFields(params.charge),
  };
}

/**
 * The `GroupedBatchMatchApproval` body for a grouped/ETF approve funded from the COMPANY WALLET. Same
 * shape/route as {@link buildApproveGroupedBody} but `payment_method: 'Wallet'` and `card_id: null`.
 * ONE wallet withdraw over `amount` funds the whole group; the backend splits it across charities.
 */
export function buildApproveWalletGroupedBody(params: {
  matchIds: string[];
  amount: number;
  disclosure?: ResolvedDisclosure | null;
  charge?: ApprovalCharge | null;
}): {
  match_ids: string[];
  amount: number;
  payment_method: typeof WALLET_PAYMENT_METHOD;
  card_id: null;
  alternative_payment_method_token: Record<string, never>;
  metadata: Record<string, never>;
} & DisclosureEchoFields &
  ApprovalChargeFields {
  return {
    match_ids: params.matchIds,
    amount: params.amount,
    payment_method: WALLET_PAYMENT_METHOD,
    card_id: null,
    alternative_payment_method_token: {},
    metadata: {},
    ...disclosureEcho(params.disclosure),
    ...approvalChargeFields(params.charge),
  };
}

// ── Company-wallet funding source (for the approve dialog's "Pay with" option) ────────────────────
//
// The approve dialog can fund a match from a saved card OR the company wallet. The wallet option's
// availability + label are driven by `GET /admin/company/wallet` (`WalletOut`). The SPENDABLE amount
// mirrors the backend's wallet gate (`gross_amount > available_balance` in
// `charge_or_withdraw_for_owner_with_intent`): use `available_balance` (balance − active holds) when
// the wire carries it, else fall back to `balance`. Money is NEVER float-parsed — the display comes
// from `formatMoneyWithSymbol` (string math) and the covers-amount comparison runs in exact integer
// cents via `parseCentsAmount`.

/** The company wallet reduced to what the "Pay with" wallet option needs: a display + a cents amount. */
export interface WalletFundingOption {
  /** The spendable balance formatted with a leading `$` (e.g. "$10.00"). */
  balanceDisplay: string;
  /** The spendable balance in EXACT integer cents, for the covers-this-match comparison. */
  availableCents: number;
}

/**
 * Map a `WalletOut` (or undefined while the query is pending/errored) to the funding option, or `null`
 * when there is no usable/parseable wallet balance — in which case the dialog simply omits the wallet
 * option and stays card-only (tolerant degradation, never a throw). The spendable amount is
 * `available_balance` when present + parseable, else `balance` (mirroring the backend gate + the
 * Settings balance card).
 */
export function toWalletFunding(wallet: WalletOut | undefined): WalletFundingOption | null {
  if (!wallet) return null;
  const availableRaw =
    typeof wallet.available_balance === 'string' && wallet.available_balance.trim().length > 0
      ? wallet.available_balance
      : null;
  const balanceRaw = typeof wallet.balance === 'string' ? wallet.balance : null;
  const spendableRaw = availableRaw ?? balanceRaw;
  if (spendableRaw === null) return null;
  const availableCents = parseCentsAmount(spendableRaw);
  const balanceDisplay = formatMoneyWithSymbol(spendableRaw);
  if (availableCents === null || balanceDisplay === null) return null;
  return { balanceDisplay, availableCents };
}

/**
 * Whether the wallet's spendable balance covers a match `amount` (dollars, cents allowed). Compared
 * in exact integer cents, never a float — mirrors the backend `gross_amount > available` gate for the
 * `cover_fees=false` case, where the withdraw equals the match. Once the wallet is priced, the
 * approve dialog compares the gross it states instead, which covering the fees raises (CAM-R80); the
 * backend's 409 remains the authority.
 */
export function walletCoversAmount(funding: WalletFundingOption, amount: number): boolean {
  return funding.availableCents >= chargeableCents(amount);
}

/** The "Pay with" wallet option label — balance-aware, with a "(too low)" hint when it can't cover. */
export function walletOptionLabel(funding: WalletFundingOption, coversAmount: boolean): string {
  return coversAmount
    ? `Company wallet — ${funding.balanceDisplay} available`
    : `Company wallet — ${funding.balanceDisplay} available (too low)`;
}

// ── Pending-row grouping (for Approve all / grouped reject) ───────────────────────────────────────
//
// The grouped approve/reject/budget routes take one request's legs per call (the backend
// `_resolve_grouped_post_id` + `_validate_grouped_batch` reject a heterogeneous batch), so "Approve
// all" cannot be one call over every pending match — it must fan out over the DISTINCT requests.

/** One approvable unit: a standalone single match, or one split gift's legs. */
export interface ApprovalGroup {
  /** 'single' → the `{match_id}/approve` route; 'grouped' → the grouped route. */
  kind: 'single' | 'grouped';
  /** The match ids in this unit (one for a single; ≥1 for grouped) — its identity, and its routes'. */
  matchIds: string[];
  /**
   * What Approve all charges for this unit (dollars, cents allowed): its row's figure (EP-BE19).
   * Waiting requests claim the match budgets in request order, so the figures never promise a
   * budget twice and the units' sum is the batch's total. `null` → not chargeable; never charged.
   */
  amountValue: number | null;
  /**
   * Who the unit pays — the charity's name, or the grouped row's title — so Approve all can say
   * which disclosure governs which match when its units resolve to different bodies (CAM-X4 web).
   * Optional: a unit built without one is simply unlabelled.
   */
  name?: string | null;
  /** The recipient's logo for Approve all's list: a single's charity, a named collection's cover;
   *  none on any other grouped row's wire. */
  logoUrl?: string | null;
  /** The recipient's mark is the neutral lock: a collection it does not name (EP-WEB40). */
  markLocked?: boolean;
  /** The employee whose gift the unit matches, as Approve all's list words it inside its sentence
   *  ("Matching {…}"): their name, or "an anonymous employee" (`employeeInSentence`). */
  employeeName?: string | null;
  /** The unit's display amount ("$50") — what Approve all matches for it — for Approve all's list. */
  amountLabel?: string | null;
  /** A grouped row's wire kind ('ETF' / 'Fundraiser'), or null. */
  kindLabel?: string | null;
}

/**
 * The product's own name for a grouped request's kind (the wire's 'ETF' is a Cause ETF to a donor),
 * or null when the wire does not declare one (a collection).
 */
export function groupKindName(kindLabel: string | null | undefined): string | null {
  if (kindLabel === 'ETF') return 'Cause ETF';
  if (kindLabel === 'Fundraiser') return 'Fundraiser';
  return null;
}

/** The figure Approve all charges a waiting row at, in exact cents, or null when it leaves it out. */
function approveAllCents(r: MatchApprovalRow): number | null {
  return r.status === 'Pending' && r.figure?.kind === 'available' ? r.figure.cents : null;
}

/** The earliest parseable instant among a request's legs' `created_at`, in epoch ms. */
function earliestRequestMs(createdAts: readonly (string | null | undefined)[]): number | null {
  let earliest: number | null = null;
  for (const at of createdAts) {
    const ms = at ? Date.parse(at) : Number.NaN;
    if (Number.isFinite(ms) && (earliest === null || ms < earliest)) earliest = ms;
  }
  return earliest;
}

/** Request order (D-EP-56): the oldest request first, ties by its earliest match id — the order the
 *  server claims the budgets in. A row whose time does not parse goes last. */
function byRequestOrder(a: MatchApprovalRow, b: MatchApprovalRow): number {
  const at = (r: MatchApprovalRow) => r.requestedAtMs ?? Number.POSITIVE_INFINITY;
  if (at(a) !== at(b)) return at(a) < at(b) ? -1 : 1;
  const firstId = (r: MatchApprovalRow) => [...r.matchIds].sort()[0] ?? '';
  return firstId(a) < firstId(b) ? -1 : firstId(a) > firstId(b) ? 1 : 0;
}

/**
 * Map the PENDING display rows Approve all can approve onto its units 1:1 — the decode already
 * collapsed grouped legs into one row, so every such row IS one unit: a single row → the
 * `{match_id}/approve` route; a grouped row → the grouped route with its complete member set. Each
 * unit is charged at its row's figure, in REQUEST ORDER (oldest first) — the order the server's
 * figures claim the budgets in (D-EP-56), so the batch's total is the sum of the rows. A waiting row
 * with nothing left for it (its budget used up, or no figure on the wire) is left out
 * (`pendingLeftOutOfApproveAll`).
 */
export function groupPendingRows(rows: MatchApprovalRow[]): ApprovalGroup[] {
  return waitingRequests(rows).filter((unit) => unit.amountValue !== null);
}

/**
 * Every WAITING row as an Approve-all unit, in request order (oldest first): at its figure, or with
 * `amountValue` null when Approve all leaves it out. What the approve dialog re-prices at the figures
 * now when it opens (EP-WEB41), so a request left out on the list can join once something is left
 * for it.
 */
export function waitingRequests(rows: MatchApprovalRow[]): ApprovalGroup[] {
  return [...rows]
    .filter((r) => r.status === 'Pending')
    .sort(byRequestOrder)
    .map((r) => {
      const cents = approveAllCents(r);
      return {
        kind: r.kind,
        matchIds: r.matchIds,
        amountValue: cents === null ? null : cents / 100,
        name: r.kind === 'grouped' ? groupRowTitle(r) : r.charity.name,
        logoUrl:
          r.kind === 'single' ? (r.charity.logo_url ?? null) : (r.collection?.coverUrl ?? null),
        markLocked: r.kind === 'grouped' && r.collection !== null && collectionLocked(r.collection),
        employeeName: employeeInSentence(r),
        amountLabel: cents === null ? null : formatCentsUsd(cents),
        kindLabel: r.kind === 'grouped' ? r.groupKindLabel : null,
      };
    });
}

/** How many waiting requests Approve all leaves out (nothing left for them, or no figure). */
export function pendingLeftOutOfApproveAll(rows: MatchApprovalRow[]): number {
  return rows.filter((r) => r.status === 'Pending' && approveAllCents(r) === null).length;
}

/**
 * The total across every approvable pending group, or `null` if any group is non-chargeable. Summed
 * in EXACT integer cents — a float sum of cent-carrying groups drifts (0.1 + 0.2), and this number
 * is charged.
 */
export function totalApprovableAmount(groups: ApprovalGroup[]): number | null {
  let cents = 0;
  for (const g of groups) {
    if (g.amountValue === null) return null;
    cents += chargeableCents(g.amountValue);
  }
  return cents / 100;
}

/**
 * USD label for a chargeable approval amount: whole dollars when it lands on one ("$50"), exact
 * cents otherwise ("$590.24"). Never rounds — this labels the number about to be charged, and the
 * confirm copy must state it exactly.
 */
export function formatApprovalUsd(amount: number | null): string {
  if (amount === null || !Number.isFinite(amount)) return '—';
  return formatCentsUsd(chargeableCents(amount));
}

/**
 * Whole-dollar USD label for an amount that is whole BY CONSTRUCTION — the Give surface, where the
 * admin types the integer being given. ★ Not for approvals: an approval amount comes from a donation
 * and can carry cents, which this would silently round away (use `formatApprovalUsd`).
 */
export function formatWholeDollarUsd(amount: number | null): string {
  if (amount === null || !Number.isFinite(amount)) return '—';
  return USD.format(amount);
}

/** Charity website href from `organization_domain` (or null when absent). */
export function charityWebsiteHref(charity: CharityOut): string | null {
  const domain = charity.organization_domain?.trim();
  if (!domain) return null;
  return /^https?:\/\//i.test(domain) ? domain : `https://${domain}`;
}
